import { useCallback, useEffect, useId, useState } from 'react';
import { RichEditor, tiptapDocToPlainText } from '@odo/shared-ui';
import type { RichEditorImageUploadHandler } from '@odo/shared-ui';
import {
    useAddCommunicationEntry,
    useUpdateCommunicationEntry
} from '../../../../hooks/useCommunications';
import type { CommunicationChannel, CommunicationEntry } from '../../../../services/communications';
import { uploadPracticeImage } from '../../../../services/practiceImages';
import { useToast } from '../../../../providers/ToastProvider';
import { apiErrorMessage } from '../../../../utils/apiError';
import { BodySeedChips } from './BodySeedChips';
import { ChannelSegmentedControl } from './ChannelSegmentedControl';
import { channelDef } from './channels';
import { EntryBodyCharCount } from './EntryBodyCharCount';
import { EntryFieldsSection } from './EntryFieldsSection';
import { EntryTitleInput } from './EntryTitleInput';
import {
    buildCreateEntryInput,
    buildUpdateEntryInput,
    durationErrorFor,
    emptyEntryFormValues,
    entryBodyWouldFailValidation,
    entryFormValuesDiffer,
    entryFormValuesFromEntry
} from './entryFormValues';
import { MAX_ENTRY_BODY_LENGTH } from './entryBodyLimit';
import { useCallTimer } from './useCallTimer';

interface EntryComposerProps {
    communicationId: string;
    practiceId: string | null;
    /** Present when editing an existing interaction - absent when logging a new one. */
    entry?: CommunicationEntry;
    /** Only meaningful when `entry` is absent - the channel picked to start a new interaction. */
    initialChannel?: CommunicationChannel;
    onDone: () => void;
    onCancel: () => void;
    /** Fires whenever the composer's dirty state changes - lets the timeline guard switching to another interaction while this one has unsaved edits. */
    onDirtyChange?: (dirty: boolean) => void;
}

// The thread detail page's interaction composer - same chrome as the create
// page's entry card, used for both adding and inline-editing an entry.
export function EntryComposer({
    communicationId,
    practiceId,
    entry,
    initialChannel = 'call',
    onDone,
    onCancel,
    onDirtyChange
}: EntryComposerProps) {
    const { showToast } = useToast();
    const missingBodyHintId = useId();
    const isEdit = Boolean(entry);
    const initialValues = useState(() =>
        entry ? entryFormValuesFromEntry(entry) : emptyEntryFormValues(initialChannel)
    )[0];
    const [values, setValues] = useState(initialValues);
    const timer = useCallTimer();
    const addEntry = useAddCommunicationEntry();
    const updateEntry = useUpdateCommunicationEntry();

    const isSaving = isEdit ? updateEntry.isPending : addEntry.isPending;
    const durationError = durationErrorFor(values.durationMinutesDisplay);
    const plainBodyLength = tiptapDocToPlainText(values.doc).trim().length;
    const overLimit = plainBodyLength > MAX_ENTRY_BODY_LENGTH;
    const missingBodyText = entryBodyWouldFailValidation(initialValues.doc, values.doc);
    const isDirty = entryFormValuesDiffer(initialValues, values);
    const canSave =
        !durationError && !overLimit && !missingBodyText && !isSaving && (!isEdit || isDirty);

    useEffect(() => {
        onDirtyChange?.(isDirty);
        // onDirtyChange omitted: rarely memoized by callers, would refire needlessly.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDirty]);

    const handleImageUpload: RichEditorImageUploadHandler = useCallback(
        async (file) => {
            if (!practiceId) {
                throw new Error(
                    'This thread has no practice yet - select one before adding images.'
                );
            }
            const { url, fileId } = await uploadPracticeImage(practiceId, file, 'call');
            return { url, fileId };
        },
        [practiceId]
    );

    const onToggleTimer = useCallback(() => {
        if (timer.isRunning) {
            const minutes = timer.stop();
            if (minutes !== null) {
                setValues((v) => ({ ...v, durationMinutesDisplay: String(minutes) }));
            }
        } else {
            timer.start();
        }
    }, [timer]);

    const handleChannelChange = (channel: CommunicationChannel) => {
        setValues((v) => {
            const def = channelDef(channel);
            const hadDirection = channelDef(v.channel).hasDirection;
            return {
                ...v,
                channel,
                // No "empty" direction, so reset to the default instead.
                direction: !hadDirection && def.hasDirection ? 'incoming' : v.direction,
                durationMinutesDisplay: def.hasDuration ? v.durationMinutesDisplay : '',
                contactDisplay: def.contactLabel ? v.contactDisplay : '',
                contactNameDisplay: def.contactNameLabel ? v.contactNameDisplay : ''
            };
        });
    };

    const handleSave = () => {
        if (!canSave) return;
        let effective = values;
        if (timer.isRunning) {
            const minutes = timer.stop();
            if (minutes !== null) {
                effective = { ...values, durationMinutesDisplay: String(minutes) };
                setValues(effective);
            }
        }

        if (isEdit && entry) {
            const patch = buildUpdateEntryInput(initialValues, effective);
            if (Object.keys(patch).length === 0) {
                onDone();
                return;
            }
            updateEntry.mutate(
                { id: communicationId, entryId: entry.id, patch },
                {
                    onSuccess: () => {
                        showToast('Interaction updated', 'success');
                        onDone();
                    },
                    onError: (err) =>
                        showToast(apiErrorMessage(err, 'Failed to update interaction'), 'error')
                }
            );
            return;
        }

        addEntry.mutate(
            { id: communicationId, input: buildCreateEntryInput(effective) },
            {
                onSuccess: () => {
                    showToast('Interaction added', 'success');
                    onDone();
                },
                onError: (err) =>
                    showToast(apiErrorMessage(err, 'Failed to add interaction'), 'error')
            }
        );
    };

    return (
        <div className="bg-white border border-gray-400 rounded-xl overflow-hidden flex flex-col">
            {/* Headline row divider is inset (mx-5) to underline just the
                content area; the border-b below separates the whole header
                block from the body and stays full-width. */}
            <div className="border-b border-gray-100 flex flex-col">
                <div className="px-5 pt-4 pb-3.5">
                    <EntryTitleInput
                        value={values.titleDisplay}
                        onChange={(titleDisplay) => setValues((v) => ({ ...v, titleDisplay }))}
                        disabled={isSaving}
                    />
                </div>

                <div className="mx-5 border-b border-gray-100" />

                <div className="px-5 pt-3.5 pb-3.5 flex flex-col gap-3.5">
                    <ChannelSegmentedControl
                        value={values.channel}
                        onChange={handleChannelChange}
                        disabled={isSaving}
                    />

                    <EntryFieldsSection
                        values={values}
                        setValues={setValues}
                        disabled={isSaving}
                        durationError={durationError}
                        timerIsRunning={timer.isRunning}
                        timerElapsedLabel={timer.elapsedLabel}
                        onToggleTimer={onToggleTimer}
                    />
                </div>
            </div>

            {/* Fixed min-height, not RichEditor's flex-1: this composer sits
                in normal document flow, not a height-constrained column. */}
            <div className="px-5 py-4">
                <div
                    className={`border rounded-lg overflow-hidden flex flex-col min-h-[280px] focus-within:border-gray-400 transition-colors ${
                        missingBodyText ? 'border-red-300' : 'border-gray-200'
                    }`}
                >
                    <RichEditor
                        value={values.doc}
                        onChange={(doc) => setValues((v) => ({ ...v, doc }))}
                        onImageUpload={handleImageUpload}
                        placeholder="What happened?"
                        chrome="flush"
                        readOnly={isSaving}
                        toolbarRightSlot={<EntryBodyCharCount length={plainBodyLength} />}
                        belowToolbarSlot={
                            <BodySeedChips
                                doc={values.doc}
                                channel={values.channel}
                                onInsert={(doc) => setValues((v) => ({ ...v, doc }))}
                                disabled={isSaving}
                            />
                        }
                    />
                </div>
                {missingBodyText && (
                    <p
                        id={missingBodyHintId}
                        role="alert"
                        className="text-[11px] text-red-500 mt-1.5"
                    >
                        Add a note before saving
                    </p>
                )}
            </div>

            <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-end gap-2.5">
                <button
                    type="button"
                    onClick={onCancel}
                    disabled={isSaving}
                    className="h-9 px-3 rounded-lg text-[13px] font-semibold text-gray-500 hover:text-gray-900 disabled:opacity-50"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    onClick={handleSave}
                    disabled={!canSave}
                    aria-describedby={missingBodyText ? missingBodyHintId : undefined}
                    className="h-9 px-4 rounded-lg bg-black text-white text-[13px] font-semibold hover:bg-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {isEdit ? 'Save interaction' : 'Add interaction'}
                </button>
            </div>
        </div>
    );
}
